// Core configuration types
export interface VijiCoreConfig {
  /** Container element where the scene will be rendered */
  hostContainer: HTMLElement;
  
  /** Artist scene code to execute */
  sceneCode: string;
  
  /** Frame rate mode - 'full' for every animation frame, 'half' for every second frame */
  frameRateMode?: FrameRateMode;
  
  /** Enable automatic performance optimization */
  autoOptimize?: boolean;
  
  /** Audio input stream */
  audioStream?: MediaStream;
  
  /** Video input stream */
  videoStream?: MediaStream;
  
  /** Audio analysis configuration */
  analysisConfig?: AnalysisConfiguration;
  
  /** Initial parameter values */
  parameters?: ParameterGroup[];
  
  /** Disable input processing (for preview instances) */
  noInputs?: boolean;
  
  /** Enable user interaction events */
  allowUserInteraction?: boolean;
}

// Capture frame options
export interface CaptureFrameOptions {
  /** MIME type for output, e.g., 'image/png', 'image/jpeg', 'image/webp' */
  type?: string;
  /**
   * Target resolution.
   * - number: scale factor relative to current canvas size (e.g., 0.5 = 50%)
   * - { width, height }: exact output size; if aspect ratio differs from canvas,
   *   the source is center-cropped to match the target aspect ratio before scaling
   */
  resolution?: number | { width: number; height: number };
}

// Core capabilities state
export interface CoreCapabilities {
  /** Audio input is active and connected */
  hasAudio: boolean;
  
  /** Video input is active and connected */
  hasVideo: boolean;
  
  /** User interactions (mouse, touch, keyboard) are enabled */
  hasInteraction: boolean;
  
  /** General parameters are always available */
  hasGeneral: boolean;
}

// Resolution types
export type Resolution = { width: number; height: number; }

// Parameter category system
export type ParameterCategory = 'audio' | 'video' | 'interaction' | 'general';

// Enhanced parameter system types for Phase 2
export interface ParameterGroup {
  groupName: string;
  category?: ParameterCategory;
  description?: string;
  collapsed?: boolean;
  order?: number;
  parameters: Record<string, ParameterDefinition>;
}

export interface ParameterDefinition {
  type: 'slider' | 'color' | 'toggle' | 'select' | 'text' | 'number';
  defaultValue: any;
  label?: string;
  description?: string;
  group?: string;
  category?: ParameterCategory;
  config?: ParameterConfig;
  // Validation function
  validate?: (value: any) => boolean;
}

export interface ParameterConfig {
  // For slider/number types
  min?: number;
  max?: number;
  step?: number;
  
  // For select type
  options?: string[] | number[];
  
  // For text type
  maxLength?: number;
  
  // UI configuration
  label?: string;
  description?: string;
  hidden?: boolean;
  
  // Advanced configuration
  precision?: number; // For number display precision
}

// Parameter object types (returned by helper functions)
export interface SliderParameter {
  value: number;
  min: number;
  max: number;
  step: number;
  label: string;
  description?: string;
  group: string;
  category: ParameterCategory;
}

export interface ColorParameter {
  value: string;
  label: string;
  description?: string;
  group: string;
  category: ParameterCategory;
}

export interface ToggleParameter {
  value: boolean;
  label: string;
  description?: string;
  group: string;
  category: ParameterCategory;
}

export interface SelectParameter {
  value: string | number;
  options: string[] | number[];
  label: string;
  description?: string;
  group: string;
  category: ParameterCategory;
}

export interface TextParameter {
  value: string;
  maxLength?: number;
  label: string;
  description?: string;
  group: string;
  category: ParameterCategory;
}

export interface NumberParameter {
  value: number;
  min: number;
  max: number;
  step: number;
  label: string;
  description?: string;
  group: string;
  category: ParameterCategory;
}

// Parameter helper function configurations
export interface SliderConfig {
  min?: number;
  max?: number;
  step?: number;
  label: string;
  description?: string;
  group?: string;
  category?: ParameterCategory;
}

export interface ColorConfig {
  label: string;
  description?: string;
  group?: string;
  category?: ParameterCategory;
}

export interface ToggleConfig {
  label: string;
  description?: string;
  group?: string;
  category?: ParameterCategory;
}

export interface SelectConfig {
  options: string[] | number[];
  label: string;
  description?: string;
  group?: string;
  category?: ParameterCategory;
}

export interface TextConfig {
  label: string;
  description?: string;
  group?: string;
  category?: ParameterCategory;
  maxLength?: number;
}

export interface NumberConfig {
  min?: number;
  max?: number;
  step?: number;
  label: string;
  description?: string;
  group?: string;
  category?: ParameterCategory;
}

// Parameter value types
export type ParameterValue = string | number | boolean;

// Parameter update message data
export interface ParameterUpdateData {
  name: string;
  value: ParameterValue;
  timestamp: number;
}

export interface ParameterBatchUpdateData {
  updates: ParameterUpdateData[];
  timestamp: number;
}

// Parameter definition extraction data
export interface ParameterDefinitionData {
  groups: ParameterGroup[];
  timestamp: number;
}

// Audio analysis configuration
export interface AnalysisConfiguration {
  fftSize?: number;
  smoothing?: number;
  frequencyBands?: FrequencyBand[];
  beatDetection?: boolean;
  onsetDetection?: boolean;
}

export interface FrequencyBand {
  name: string;
  min: number;
  max: number;
}

// Frame rate mode types
export type FrameRateMode = 'full' | 'half';

export interface FrameRateInfo {
  mode: FrameRateMode;
  screenRefreshRate: number;
  effectiveRefreshRate: number;
}

// Performance monitoring
export interface PerformanceStats {
  frameTime: number;
  resolution: Resolution;
  scale: number;
  frameRate: FrameRateInfo;
  memoryUsage?: number;
  activeStreams?: number;
  parameterCount?: number;
}

// Communication message types
export interface Message {
  type: string;
  id: string;
  timestamp: number;
  data?: any;
}

export interface HostToWorkerMessage extends Message {
  type:
    | 'init'
    | 'parameter-update'
    | 'parameter-batch-update'
    | 'stream-update'
    | 'performance-update'
    | 'capture-frame';
}

export interface WorkerToHostMessage extends Message {
  type:
    | 'ready'
    | 'parameters-defined'
    | 'parameter-validation-error'
    | 'error'
    | 'performance-warning'
    | 'capture-frame-result';
}

// Resource management types
export interface ResourceTracker {
  workers: Set<Worker>;
  iframes: Set<HTMLIFrameElement>;
  streams: Set<MediaStream>;
  contexts: Set<AudioContext | CanvasRenderingContext2D | WebGLRenderingContext>;
}

// Enhanced Artist API types (provided to artist code)
export interface VijiAPI {
  // Canvas and rendering
  canvas: OffscreenCanvas;
  ctx?: OffscreenCanvasRenderingContext2D;
  gl?: WebGL2RenderingContext;
  width: number;
  height: number;
  pixelRatio: number;
  
  // Timing
  time: number;
  deltaTime: number;
  frameCount: number;
  fps: number;
  
  // Audio (when available)
  audio: AudioAPI;
  
  // Video (when available)  
  video: VideoAPI;
  
  // User interaction
  mouse: MouseAPI;
  keyboard: KeyboardAPI;
  touches: TouchAPI[];
  
  // Parameter helper functions (return parameter objects)
  slider: (defaultValue: number, config: SliderConfig) => SliderParameter;
  color: (defaultValue: string, config: ColorConfig) => ColorParameter;
  toggle: (defaultValue: boolean, config: ToggleConfig) => ToggleParameter;
  select: (defaultValue: string | number, config: SelectConfig) => SelectParameter;
  text: (defaultValue: string, config: TextConfig) => TextParameter;
  number: (defaultValue: number, config: NumberConfig) => NumberParameter;
  
  // Utility methods
  useContext(type: '2d' | 'webgl'): OffscreenCanvasRenderingContext2D | WebGLRenderingContext | WebGL2RenderingContext | null;
}

// Audio API
export interface AudioAPI {
  isConnected: boolean;
  volume: {
    rms: number;
    peak: number;
  };
  beat?: {
    isKick: boolean;
    confidence: number;
  };
  bands: Record<string, number>;
  getFrequencyData: () => Uint8Array;
}

// Video API
export interface VideoAPI {
  isConnected: boolean;
  currentFrame: OffscreenCanvas | null;
  frameWidth: number;
  frameHeight: number;
  frameRate: number;
  getFrameData: () => ImageData | null;
  faces: FaceData[];
  hands: HandData[];
}

export interface FaceData {
  bounds: { x: number; y: number; width: number; height: number };
  confidence: number;
  landmarks: { x: number; y: number }[];
  expressions: Record<string, number>;
}

export interface HandData {
  palm: { x: number; y: number };
  fingers: { x: number; y: number }[];
  gestures: Record<string, boolean>;
}

// Input APIs - Phase 7 Implementation
export interface MouseAPI {
  // Position (canvas coordinates)
  x: number;
  y: number;
  
  // Canvas bounds
  isInCanvas: boolean;
  
  // Button states
  isPressed: boolean;
  leftButton: boolean;
  rightButton: boolean;
  middleButton: boolean;
  
  // Movement
  velocity: { x: number; y: number };
  deltaX: number;
  deltaY: number;
  
  // Scroll wheel
  wheelDelta: number;
  wheelX: number;
  wheelY: number;
  
  // Events (frame-based)
  wasPressed: boolean;
  wasReleased: boolean;
  wasMoved: boolean;
}

export interface KeyboardAPI {
  // Key state queries
  isPressed(key: string): boolean;
  wasPressed(key: string): boolean;
  wasReleased(key: string): boolean;
  
  // Active state
  activeKeys: Set<string>;
  pressedThisFrame: Set<string>;
  releasedThisFrame: Set<string>;
  
  // Recent activity
  lastKeyPressed: string;
  lastKeyReleased: string;
  
  // Modifier keys
  shift: boolean;
  ctrl: boolean;
  alt: boolean;
  meta: boolean;
}

export interface TouchPoint {
  id: number;
  x: number;
  y: number;
  pressure: number;
  radius: number;
  radiusX: number;
  radiusY: number;
  rotationAngle: number;
  force: number;
  
  // Movement
  deltaX: number;
  deltaY: number;
  velocity: { x: number; y: number };
  
  // Lifecycle
  isNew: boolean;
  isActive: boolean;
  isEnding: boolean;
}

export interface TouchAPI {
  // Current touches
  points: TouchPoint[];
  count: number;
  
  // Touch events
  started: TouchPoint[];
  moved: TouchPoint[];
  ended: TouchPoint[];
  
  // Convenience accessors
  primary: TouchPoint | null;
  
  // Gesture detection
  gestures: TouchGestureAPI;
}

export interface TouchGestureAPI {
  // Gesture states
  isPinching: boolean;
  isRotating: boolean;
  isPanning: boolean;
  isTapping: boolean;
  
  // Gesture values
  pinchScale: number;
  pinchDelta: number;
  rotationAngle: number;
  rotationDelta: number;
  panDelta: { x: number; y: number };
  
  // Tap detection
  tapCount: number;
  lastTapTime: number;
  tapPosition: { x: number; y: number } | null;
}

// Enhanced Parameter API for Phase 2
export interface ParameterAPI {
  // Define parameters (called once outside render loop)
  define(groups: ParameterGroup[]): void;
  
  // Proxy access to parameter values (fast render-time access)
  [key: string]: any;
}

// Error types
export class VijiCoreError extends Error {
  constructor(
    message: string,
    public code: string,
    public context?: any
  ) {
    super(message);
    this.name = 'VijiCoreError';
  }
} 